import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useParams, Link } from "react-router-dom";
import SEO from "../utils/SEO";
import SocialShare from "../utils/SocialShare";
import { trackCTAClick } from "../../utils/analytics";

// Shareable, indexable landing page for a dosha result. A quiz-taker can share
// /quiz/dosha/result/pitta and it previews cleanly (per-type OG) and pulls new
// visitors back into the quiz/app — the "growth engine" mechanic in the SEO brief.
// Wellness framing only (no medical claims) per the YMYL guardrails.

export const DOSHAS = {
  vata: {
    name: "Vata",
    element: "Air & Space",
    emoji: "🌬️",
    tagline: "Creative, energetic, and always in motion.",
    summary:
      "Vata types are lively, quick-thinking, and imaginative, with a lean frame and a mind that loves movement and change. In balance you feel enthusiastic and inspired; when Vata runs high you may notice restlessness, dry skin, irregular digestion, or trouble winding down.",
    balance: [
      "Keep a steady daily rhythm — regular meals and an earlier bedtime.",
      "Favor warm, cooked, grounding foods over cold or raw.",
      "Slow down with gentle routines like walking, warm oil self-massage, and breathwork.",
    ],
    herbs: "ashwagandha, ginger, and licorice root",
  },
  pitta: {
    name: "Pitta",
    element: "Fire & Water",
    emoji: "🔥",
    tagline: "Focused, driven, and warm.",
    summary:
      "Pitta types are sharp, determined, and naturally strong digesters, with a warm and purposeful energy. In balance you feel focused and confident; when Pitta runs high you may notice irritability, heat, heartburn, or a tendency to overdo it.",
    balance: [
      "Favor cooling, hydrating foods and avoid skipping meals.",
      "Build in genuine downtime — nature, water, and unstructured rest.",
      "Ease competitiveness with calming practices like meditation or a walk.",
    ],
    herbs: "aloe vera, coriander, and peppermint",
  },
  kapha: {
    name: "Kapha",
    element: "Earth & Water",
    emoji: "🌱",
    tagline: "Calm, steady, and nurturing.",
    summary:
      "Kapha types are grounded, patient, and strong, with excellent stamina and a caring nature. In balance you feel calm and stable; when Kapha builds up you may notice sluggishness, congestion, water retention, or resistance to change.",
    balance: [
      "Favor light, warm, spiced foods over heavy or sweet ones.",
      "Get regular, invigorating movement to build energy.",
      "Embrace novelty and gently let go of what you've outgrown.",
    ],
    herbs: "ginger, turmeric, and tulsi (holy basil)",
  },
};

const DoshaResult = () => {
  const { type } = useParams();
  const key = String(type || "").toLowerCase();
  const dosha = DOSHAS[key];

  if (!dosha) {
    return (
      <Container style={{ paddingTop: "120px", paddingBottom: "100px", textAlign: "center" }}>
        <SEO
          title="Ayurvedic Dosha Results | Sarv"
          description="Discover your Ayurvedic dosha — Vata, Pitta, or Kapha — with Sarv's free dosha quiz."
          canonicalPath="/quiz"
        />
        <h1>Find your dosha</h1>
        <p>Take the free quiz to discover whether you're Vata, Pitta, or Kapha.</p>
        <Button as={Link} to="/quiz" className="business-button">
          Take the Dosha Quiz
        </Button>
      </Container>
    );
  }

  const title = `You're ${dosha.name} ${dosha.emoji} — Your Ayurvedic Dosha | Sarv`;
  const description = `${dosha.name} (${dosha.element}): ${dosha.tagline} ${dosha.summary}`.slice(
    0,
    300
  );

  return (
    <Container style={{ paddingTop: "110px", paddingBottom: "90px", maxWidth: "820px" }}>
      <SEO
        title={title}
        description={description}
        canonicalPath={`/quiz/dosha/result/${key}`}
        keywords={`${dosha.name} dosha, Ayurveda ${dosha.name}, ${dosha.name} body type, dosha quiz result, ${dosha.name} balancing herbs`}
      />

      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <p className="mission-label">Your Ayurvedic Dosha</p>
        <h1 style={{ fontFamily: "Canela", color: "#0a0a39" }}>
          You're {dosha.name} {dosha.emoji}
        </h1>
        <p style={{ fontSize: "20px", color: "#165577" }}>
          {dosha.element} · {dosha.tagline}
        </p>
      </div>

      <Row>
        <Col>
          <p style={{ fontSize: "18px", lineHeight: 1.6 }}>{dosha.summary}</p>

          <h2 style={{ fontFamily: "Canela", marginTop: "28px" }}>
            How to keep {dosha.name} in balance
          </h2>
          <ul style={{ fontSize: "18px", lineHeight: 1.7 }}>
            {dosha.balance.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ul>

          <p style={{ fontSize: "18px", lineHeight: 1.6, marginTop: "20px" }}>
            Herbs often used to support {dosha.name} types include {dosha.herbs}. Sarv builds
            personalized, natural recommendations around your unique constitution and goals.
          </p>
          <p style={{ fontSize: "13px", color: "#888" }}>
            For general wellness and educational purposes only — not medical advice.
          </p>

          <div className="d-flex flex-wrap gap-3 justify-content-center my-4">
            <Button as={Link} to="/quiz" variant="outline-primary">
              Retake the quiz
            </Button>
            <Button
              as={Link}
              to={`/start?source=dosha-${key}`}
              className="business-button"
              onClick={() => trackCTAClick("dosha_result_start_trial", `dosha-${key}`)}
            >
              Get my personalized plan &rarr;
            </Button>
          </div>

          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <p style={{ marginBottom: "8px", color: "#555" }}>Share your result</p>
            <SocialShare />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default DoshaResult;
